<script>
import {computed, defineComponent} from "vue";

import Icon from "./Icon.vue";

export default defineComponent({
  // Right side drawer. Can't have relative parent or it will break
  name: "Drawer",
  components: {Icon},
  props: {
    size: {
      type: String,
      default: "large",
      validator: (value) => {
        return ['large', 'small'].includes(value);
      }
    },
    hideOverlay: {
      type: Boolean,
      default: false
    },

    hideCloseButton: {
      type: Boolean,
      default: false
    }
  },
  emits: ['show', 'hide'],
  setup() {

  },

  data() {
    return {
      visible: false,
      fullScreen: false,
    };
  },
  computed: {
    classes() {
      return {
        'right-drawer': true,
        [`drawer--${this.size || 'large'}`]: true,
        [`drawer--full-screen`]: this.fullScreen,

      };
    }
  },
  watch: {
    visible() {
      document.body.style.overflowY = this.visible ? 'hidden' : 'auto'; // Hides background scroll behaviour
    }
  },
  mounted() {
    // Need this to ensure the drawer is visible on mobile because VH doesn't behave on mobile apparently
    window.addEventListener('resize', this.setHeight);
    this.setHeight();
  },
  unmounted() {
    window.removeEventListener('resize', this.setHeight);
  },
  methods: {
    show() {
      this.$emit('show');
      this.visible = true;
      document.body.classList.add('drawer-open');
      this.$nextTick(() => {
        if (this.$refs.drawer) this.$refs.drawer.scrollTop = 0;
      });
    },
    hide() {
      this.$emit('hide');
      this.visible = false;
      document.body.classList.remove('drawer-open');
    },
    toggleFullScreen(fullScreen) {
      this.fullScreen = fullScreen;
    },

    setHeight() {
      if(this.$refs.drawer) {
        // Sets the component to the actual viewport height to get around mobile browser & keyboard issues
        let screenHeight = document.documentElement.clientHeight; // Actual device screen height
        this.$refs.drawer.style.maxHeight =  `${screenHeight}px`;
        this.$refs.drawer.style.height =  `${screenHeight}px`;
      }
    }
  }
});

</script>

<template>
  <div
    ref="drawer"
    :class="[{'visible': visible}, classes]"
    class="relative"
  >
    <div
      class="m-4 text-right absolute top-0 right-0"
    >
      <button
        v-if="!hideCloseButton"
        class="close"
        @click="hide"
      >
        <icon
          icon="x"
          size="32px"
          color="black"
        />
      </button>
    </div>
    <slot />
  </div>
  <div
    v-if="!hideOverlay"
    class="drawer-mask"
    :class="[{'visible': visible}]"
    @click="hide"
  />
</template>

<style lang="scss" scoped>
.right-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 0; /* hidden initially */
  overflow: hidden;
  padding-left: 0;
  border-left: 1px solid whitesmoke;
  background: white;
  z-index: 9999;
  transition: width 0.4s;
  max-width: 100vw;

  &.visible {
    overscroll-behavior: contain;

    &.drawer--large {
      width: 100vw;

      @screen sm {
        width: 75vw;
      }

      @screen md {
        width: 60vw;
      }

      @screen lg {
        width: 40vw;
      }
    }

    &.drawer--small {
      width: 30vw;
      padding-left: 10px;

      @screen sm {
        width: 20vw;
      }

      @screen md {
        width: 15vw;
      }

      @screen lg {
        width: 10vw;
      }
    }

    &.drawer--full-screen {
      width: 100dvw !important;
      height: 100dvh;
      min-height: 100% !important;
    }
  }
}

.drawer-mask {
  position: fixed;
  left: 0;
  top: 0;
  width: 0; /* initially */
  height: 100vh;
  max-width: 100vw;
  overflow: hidden;
  background: #000;
  opacity: 0;
  z-index: 9998;
  padding: 0 !important;
  margin: 0 !important;

  &.visible {
    opacity: 0.6;
    width: 100%;
    overscroll-behavior: contain;

  }
}
</style>
